import { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';

export default function BookingSuccessPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [state, setState] = useState('checking'); // checking | paid | expired | error
  const [booking, setBooking] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState('error'); return; }
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const res = await api.get(`/retreats/booking-status/${sessionId}`);
        const d = res.data;
        if (d.payment_status === 'paid') {
          setBooking(d);
          setState('paid');
          return;
        }
        if (d.payment_status === 'expired') { setState('expired'); return; }
      } catch {
        setState('error');
        return;
      }
      if (attempts.current >= 12) { setState('expired'); return; }
      timer = setTimeout(poll, 2500);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  const sym = { gbp: '£', usd: '$', eur: '€' }[booking?.currency] || '£';

  return (
    <div className="min-h-screen bg-[#faf7f1] flex items-center justify-center px-4" data-testid="booking-success-page">
      <div className="max-w-md w-full bg-white rounded-3xl border border-[#e2d8c3] shadow-xl p-10 text-center">
        {state === 'checking' && (
          <div data-testid="booking-checking">
            <Loader2 className="w-12 h-12 mx-auto mb-6 animate-spin text-[#b08d2f]" />
            <h1 className="text-2xl font-bold text-[#1c1710] mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Confirming your booking…
            </h1>
            <p className="text-[#8d7f68] text-sm">Please don't close this page — we're checking your payment with Stripe.</p>
          </div>
        )}

        {state === 'paid' && booking && (
          <div data-testid="booking-confirmed">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#b08d2f]/10 flex items-center justify-center">
              <CheckCircle2 className="w-9 h-9 text-[#b08d2f]" />
            </div>
            <h1 className="text-2xl font-bold text-[#1c1710] mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              You're booked{booking.name ? `, ${booking.name.split(' ')[0]}` : ''}!
            </h1>
            <p className="text-[#8d7f68] mb-6">We can't wait to welcome you to The Country House.</p>
            <div className="rounded-2xl bg-[#faf7f1] border border-[#e2d8c3] p-5 text-left space-y-2 mb-8">
              <p className="font-semibold text-[#1c1710]">{booking.retreat_title}</p>
              <p className="text-sm text-[#8d7f68]">{booking.option_label} × {booking.quantity}</p>
              <p className="text-sm font-bold text-[#1c1710]">
                Paid: {sym}{Number(booking.amount).toLocaleString('en-GB')}
              </p>
            </div>
            <Link to={`/sites/${slug}`}>
              <Button className="rounded-full px-8 py-6 bg-[#1c1710] hover:bg-[#3a2f1e] text-white" data-testid="back-to-site-btn">
                Back to the site
              </Button>
            </Link>
          </div>
        )}

        {(state === 'expired' || state === 'error') && (
          <div data-testid="booking-failed">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-500/10 flex items-center justify-center">
              <XCircle className="w-9 h-9 text-red-500" />
            </div>
            <h1 className="text-2xl font-bold text-[#1c1710] mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {state === 'expired' ? "Payment not completed" : "Something went wrong"}
            </h1>
            <p className="text-[#8d7f68] mb-8">
              {state === 'expired'
                ? "Your payment session expired or wasn't completed. No money has been taken — please try again."
                : "We couldn't find this booking. If you were charged, contact us and we'll sort it immediately."}
            </p>
            <Link to={`/sites/${slug}`}>
              <Button className="rounded-full px-8 py-6 bg-[#1c1710] hover:bg-[#3a2f1e] text-white">
                Back to the site
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
